import { createStitches } from '@stitches/react';
import type * as Stitches from '@stitches/react';

export const { styled, css, globalCss, keyframes, config } = createStitches({
    theme: {
        colors: {
            primary: '#7F53FF',
            secondary: '#3619B0',
            gray: '#979797',
            info: '#41516E',
            white: '#ffffff',
            pureBlack: '#000000',
            black: '#202020',
            link: '#0047FF',
            error: '#F43636',
            warning: '#FAAD14',
            success: '#007F1C',
            buttonHover: '#724BE6',
            focus: '#EBECF0',
            iconColor: '#9D94B8',
            disableText: '#8A8A8A',
            textDefault: '#04192E',
            TextSubdued: '#676767',
            TextDisabled: '#8A8A8A',
            MenuText: '#41516E',
            HelperText: '#41516E',
            border1: '#E5E5E5',
            border2: '#E0E7FF',
            border3: '#D9D9D9',
            border4: '#C4C4C4',
            blueJeans: '#53B6FF',
            steelBlue: '#3A7FB3',
            lavenderIndigo: '#9B53FF',
            LitePrimary: '#E5DDFF',
            purple200: 'rgba(127, 83, 255, 0.2)',
            primaryLite: '#E5DDFF',
            inputBg: '#F0F0F0',
        },
        space: {
            0: '0px',
            1: '4px',
            2: '8px',
            3: '12px',
            4: '16px',
            5: '20x',
            6: '24px',
            7: '28px',
            8: '32px',
            9: '36px',
            10: '40px',
        },
        fonts: {
            untitled: 'Untitled Sans, -apple-system, system-ui, sans-serif',
            montserrat: 'Montserrat, sans-serif',
            inter: 'Inter, sans-serif',
        },
        shadows: {
            primary: '#7F53FF',
            secondary: '#3619B0',
            gray: '#979797',
            info: '#41516E',
        },
    },
    utils: {
        p: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingTop: value,
            paddingBottom: value,
            paddingLeft: value,
            paddingRight: value,
        }),
        pt: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingTop: value,
        }),
        pr: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingRight: value,
        }),
        pb: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingBottom: value,
        }),
        pl: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingLeft: value,
        }),
        px: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingLeft: value,
            paddingRight: value,
        }),
        py: (value: Stitches.PropertyValue<'padding'>) => ({
            paddingTop: value,
            paddingBottom: value,
        }),

        m: (value: Stitches.PropertyValue<'margin'>) => ({
            marginTop: value,
            marginBottom: value,
            marginLeft: value,
            marginRight: value,
        }),
        mt: (value: Stitches.PropertyValue<'margin'>) => ({
            marginTop: value,
        }),
        mr: (value: Stitches.PropertyValue<'margin'>) => ({
            marginRight: value,
        }),
        mb: (value: Stitches.PropertyValue<'margin'>) => ({
            marginBottom: value,
        }),
        ml: (value: Stitches.PropertyValue<'margin'>) => ({
            marginLeft: value,
        }),
        mx: (value: Stitches.PropertyValue<'margin'>) => ({
            marginLeft: value,
            marginRight: value,
        }),
        my: (value: Stitches.PropertyValue<'margin'>) => ({
            marginTop: value,
            marginBottom: value,
        }),

        ta: (value: Stitches.PropertyValue<'textAlign'>) => ({ textAlign: value }),

        fd: (value: Stitches.PropertyValue<'flexDirection'>) => ({ flexDirection: value }),
        fw: (value: any) => ({ flexWrap: value }),

        ai: (value: any) => ({ alignItems: value }),
        ac: (value: any) => ({ alignContent: value }),
        jc: (value: any) => ({ justifyContent: value }),
        as: (value: any) => ({ alignSelf: value }),
        fg: (value: any) => ({ flexGrow: value }),
        fs: (value: any) => ({ flexShrink: value }),
        fb: (value: any) => ({ flexBasis: value }),

        bc: (value: Stitches.PropertyValue<'backgroundColor'>) => ({
            backgroundColor: value,
        }),

        br: (value: any) => ({
            borderRadius: value,
        }),
        btrr: (value: any) => ({
            borderTopRightRadius: value,
        }),
        bbrr: (value: any) => ({
            borderBottomRightRadius: value,
        }),
        bblr: (value: any) => ({
            borderBottomLeftRadius: value,
        }),
        btlr: (value: any) => ({
            borderTopLeftRadius: value,
        }),

        bs: (value: Stitches.PropertyValue<'boxShadow'>) => ({ boxShadow: value }),

        lh: (value: Stitches.PropertyValue<'lineHeight'>) => ({ lineHeight: value }),

        ox: (value: any) => ({ overflowX: value }),
        oy: (value: any) => ({ overflowY: value }),

        pe: (value: any) => ({ pointerEvents: value }),
        us: (value: any) => ({
            WebkitUserSelect: value,
            userSelect: value,
        }),
        size: (value: Stitches.PropertyValue<'height'>) => ({
            width: value,
            height: value,
        }),
    },
    media: {
        xxs: '(min-width:320px)',
        xs: '(min-width:480px)',
        sm: '(min-width:576px)',
        md: '(min-width: 768px)',
        lg: '(min-width: 992px)',
        xl: '(min-width: 1200px)',
        xxl: '(min-width: 1400px)',
    },
});

export const IconSpinnerKeyframes = keyframes({
    '0%': {
        transform: 'rotate(0deg)',
    },
    '100%': {
        transform: 'rotate(359deg)',
    },
});
export const fadeIn = keyframes({
    '0%': { opacity: '0' },
    '100%': { opacity: '1' },
});

export const IconSpinner = css({
    animation: `${IconSpinnerKeyframes} 2s infinite linear`,
});

export const globalStyles = globalCss({
    '*': {
        margin: 0,
        padding: 0,
        fontFamily: '$inter',
        fontStyle: 'normal',
        outline: 0,
    },
});

export type CSS = Stitches.CSS<typeof config>;
